import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { CheckoutDialog } from "./CheckoutDialog";

const FEATURES = {
  gratis: ["1 video de prueba (máx. 15 s)", "Formato 16:9 o 9:16 · 480p", "Todas las voces Piper", "Subtítulos estilo TikTok", "Marca de agua"],
  creador: ["30 videos al mes", "Hasta 10 min por video", "HD 720p sin marca de agua · elige resolución", "Sube tu música y narración", "Efectos, transiciones y filtros"],
  pro: ["100 videos al mes", "Hasta 30 min por video", "Full HD 1080p · elige resolución", "Galería propia y transcripción", "Soporte prioritario"],
  agencia: ["Videos ilimitados", "Hasta 60 min por video", "Quad HD 2K (1440p) · elige resolución", "Ideal para equipos y clientes", "Soporte dedicado"],
};

export const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [checkout, setCheckout] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  const choose = (p) => {
    if (!user) return nav("/register");
    if (p.id === "gratis") return nav("/studio");
    setCheckout(p);
  };

  return (
    <div data-testid="pricing-section">
      <div className="flex justify-center mb-12">
        <div className="panel p-1 flex items-center gap-1 rounded-full" data-testid="billing-toggle">
          {[["monthly", "Mensual"], ["annual", "Anual"]].map(([k, l]) => (
            <button key={k} data-testid={`billing-${k}`} onClick={() => setCycle(k)}
              className={`px-5 py-2 rounded-full text-sm transition-colors ${cycle === k ? "bg-[#E8C47C] text-black" : "text-zinc-400 hover:text-white"}`}>
              {l}{k === "annual" && (plans.find((x) => x.id !== "gratis")?.annual_discount ?? 0.15) > 0 && <span className="ml-2 text-[10px] font-mono-tc bg-emerald-500/15 text-emerald-300 px-2 py-0.5 rounded-full">-{Math.round((plans.find((x) => x.id !== "gratis")?.annual_discount ?? 0.15) * 100)}%</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        {plans.map((p, i) => {
          const hl = p.id === "pro";
          const price = cycle === "annual" ? p.annual_monthly : p.monthly;
          const current = user && user.plan === p.id;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`} style={{ animationDelay: `${i * 90}ms` }}
              className={`fade-up relative panel p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1 ${hl ? "glow" : ""}`}>
              {hl && <span className="absolute -top-3 left-7 text-[11px] font-mono-tc bg-[#E8C47C] text-black px-3 py-1 rounded-full flex items-center gap-1"><Sparkles className="h-3 w-3" />Recomendado</span>}
              <h3 className="font-display text-lg font-bold text-white">{p.name}</h3>
              <div className="mt-5 flex items-end gap-1">
                <span className="font-display text-4xl font-extrabold text-white" data-testid={`plan-price-${p.id}`}>{money(price)}</span>
                <span className="text-zinc-500 text-sm mb-1">{p.id === "gratis" ? "para siempre" : "/mes"}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1 h-4">{cycle === "annual" && p.monthly > 0 ? `${money(p.annual)} facturado al año · ahorras ${money(p.monthly * 12 - p.annual)}` : ""}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {FEATURES[p.id].map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-zinc-300"><Check className="h-4 w-4 text-[#E8C47C] mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              <Button data-testid={`plan-cta-${p.id}`} onClick={() => choose(p)} disabled={current && p.id !== "gratis"}
                className={`mt-8 rounded-full ${hl ? "bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold" : "bg-white/5 hover:bg-white/10 text-white border border-white/10"}`}>
                {current ? "Plan actual" : p.id === "gratis" ? "Probar gratis" : `Elegir ${p.name}`}
              </Button>
            </div>
          );
        })}
      </div>
      <CheckoutDialog plan={checkout} cycle={cycle} onClose={() => setCheckout(null)} />
    </div>
  );
};
